import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import * as Yup from 'yup';

import jobsApi from '../../APIs/jobs';
import { ReactComponent as CloseSVG } from '../../assets/icons/cross.svg';
import {
  Button,
  Flex,
  FormContainer,
  TextArea,
  Typography,
} from '../../designComponents';
import { JobProcessType } from '../../types';
import { requestAPI } from '../../utils';
import { getLatestClientReview } from '../../utils/reviews';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { CloseAdditionalWrapperStyled, PDFReviewByClientStyled } from './style';

type Props = {
  jobProcess: JobProcessType;
  refetchJobProcess: () => void;
};

type InitialValuesType = {
  additionalNotes: string[];
};

const validationSchema = Yup.object().shape({
  additionalNotes: Yup.array()
    .of(Yup.string().required('Review is required.'))
    .required('At least one review is required'),
});

const PDFReviewByClient = ({ jobProcess, refetchJobProcess }: Props) => {
  const formikRef = useRef<FormikProps<InitialValuesType>>(null);
  const [modalStatus, setModalStatus] = useState<{
    open: boolean;
    status?: 'ACCEPT' | 'REJECT';
    loading: boolean;
  }>({
    open: false,
    status: undefined,
    loading: false,
  });
  const clientReview = jobProcess?.clientReview;
  const latestClientReview = clientReview
    ? getLatestClientReview(clientReview)
    : undefined;

  const handleAddAdditionalRequirement = () => {
    if (formikRef && formikRef.current) {
      const additionalNotes = formikRef.current.values.additionalNotes || [];
      const newNote = '';
      formikRef.current.setFieldValue('additionalNotes', [
        ...additionalNotes,
        newNote,
      ]);
    }
  };

  const handleRemoveAdditionalRequirement = (idx: number) => {
    if (formikRef && formikRef.current) {
      const additionalReq = formikRef.current.values.additionalNotes;

      additionalReq.splice(idx, 1);

      formikRef.current.setFieldValue('additionalNotes', [...additionalReq]);
    }
  };

  const onCloseModal = () => {
    if (modalStatus?.loading) {
      return;
    }
    setModalStatus({
      status: undefined,
      open: false,
      loading: false,
    });
  };

  const onAcceptOrReject = async (status: 'ACCEPT' | 'REJECT') => {
    if (formikRef?.current) {
      await formikRef?.current?.validateForm();
      formikRef?.current?.setFieldTouched('additionalNotes', true);
      if (Object.keys(formikRef?.current?.errors).length > 0) {
        return;
      }
    }

    setModalStatus((prev) => ({
      ...prev,
      status,
      open: true,
    }));
  };

  const handleSendReview = async (values: InitialValuesType) => {
    try {
      if (!modalStatus?.status || !latestClientReview?._id) {
        return;
      }

      setModalStatus((prev) => ({ ...prev, loading: true }));
      const formData = {
        review: values.additionalNotes,
        reviewId: latestClientReview?._id,
        reviewStatus: modalStatus?.status,
        jobProcessId: jobProcess?._id,
      };

      await requestAPI(jobsApi.reviewByClient(formData));
      onCloseModal();
      refetchJobProcess();
    } catch (error) {
      console.error('Error while sending review:', error);
    }
  };
  return (
    <PDFReviewByClientStyled>
      <Typography variant="title" fontColor="secondary" strong>
        YOUR FINAL REVIEW
      </Typography>
      <Formik
        onSubmit={handleSendReview}
        innerRef={formikRef}
        initialValues={{ additionalNotes: [''] }}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            {values.additionalNotes.map((note, index) => {
              return (
                <div key={index} style={{ maxWidth: '560px' }}>
                  {index !== 0 && (
                    <CloseAdditionalWrapperStyled>
                      <CloseSVG
                        onClick={() => handleRemoveAdditionalRequirement(index)}
                      />
                    </CloseAdditionalWrapperStyled>
                  )}

                  <FormContainer
                    label=""
                    error={
                      touched.additionalNotes && errors.additionalNotes
                        ? errors.additionalNotes[index]
                        : ''
                    }
                  >
                    <TextArea
                      key={index}
                      placeholder="Please add your review.."
                      className="note-input"
                      value={note || ''}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const { value } = e.target;
                        const updatedValues = [...values.additionalNotes];
                        updatedValues[index] = value;

                        setFieldValue('additionalNotes', updatedValues);
                      }}
                      showCount
                      maxLength={800}
                    />
                  </FormContainer>
                </div>
              );
            })}

            <Button
              variant="gray"
              type="button"
              onClick={handleAddAdditionalRequirement}
            >
              Add More Notes
            </Button>
          </Form>
        )}
      </Formik>
      <Flex gap="20px" margin="30px 0px">
        <Button variant="secondary" onClick={() => onAcceptOrReject('ACCEPT')}>
          Accept PDF
        </Button>
        <Button variant="danger" onClick={() => onAcceptOrReject('REJECT')}>
          Reject PDF
        </Button>
      </Flex>
      <ConfirmationModal
        isOpen={modalStatus.open}
        message={`Are you sure you want to ${modalStatus?.status === 'ACCEPT' ? 'accept ' : 'reject '} this plan?`}
        onClose={onCloseModal}
        okText={modalStatus?.status === 'ACCEPT' ? 'Accept' : 'Reject'}
        onOk={() => {
          if (formikRef.current) {
            formikRef.current.handleSubmit();
          }
        }}
        loading={modalStatus?.loading}
        title={modalStatus?.status === 'ACCEPT' ? 'Accept Plan' : 'Reject Plan'}
      />
    </PDFReviewByClientStyled>
  );
};

export default PDFReviewByClient;
